import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, computed, effect, inject, model, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AppstateService } from 'src/app/services/appstate.service';
import { DataService } from 'src/app/services/data.service';
import { Berechnung } from 'src/app/types/Berechnung';
import { Budgetantrag } from 'src/app/types/Budgetantrag';
import { IBerechnung } from 'src/app/types/IBerechnung';
import { IBudgetantrag } from 'src/app/types/IBudgetantrag';

@Component({
  selector: 'app-budget-baunebenkosten',
  // standalone: true,
  // imports: [],
  templateUrl: './budget-baunebenkosten.component.html',
  styleUrl: './budget-baunebenkosten.component.css'
})
export class BudgetBaunebenkostenComponent {

  static readonly BNKBERECHNUNGID: string = "bnkBerechnungID";
  static readonly BNKBERECHNUNG: string = "bnkBerechnung";
  static readonly BNKPROZENT: string = "bnkProzent";
  static readonly BNK1BIS4PROZENT: string = "bnk1bis4Prozent";
  static readonly BNK5BIS9PROZENT: string = "bnk5bis9Prozent";
  static readonly BNKVERAENDERUNG: string = "bnkVeraenderung";
  static readonly ERROR_BNKSUMME: string = "baunebenkostenSumme";
  static readonly ERROR_BNKBEGRUENDUNG: string = "baunebenkostenBegruendung";

  private readonly dialogRef = inject(MatDialogRef<BudgetBaunebenkostenComponent>);
  private readonly dataService = inject(DataService);
  private readonly appStateService = inject(AppstateService);
  private dataBudgetantrag = computed<IBudgetantrag>(() => this.dataService.budgetantragDetails());
  public dataBerechnungen = computed<IBerechnung[] | null>(() => this.dataService.budgetantragBerechnung());

  public baunebenkostenForm = new FormGroup({
                                              [BudgetBaunebenkostenComponent.BNKBERECHNUNGID]: new FormControl(0, []),
                                              [BudgetBaunebenkostenComponent.BNKBERECHNUNG]: new FormControl<IBerechnung>(new Berechnung(), []),
                                              [BudgetBaunebenkostenComponent.BNKPROZENT]: new FormControl(1, [Validators.required, Validators.min(0), Validators.max(100)]),
                                              [BudgetBaunebenkostenComponent.BNK1BIS4PROZENT]: new FormControl(1, [Validators.required, Validators.min(0), Validators.max(100)]),
                                              [BudgetBaunebenkostenComponent.BNK5BIS9PROZENT]: new FormControl(1, [Validators.required, Validators.min(0), Validators.max(100)]),
                                              [BudgetBaunebenkostenComponent.BNKVERAENDERUNG]: new FormControl(""),
                                            }, 
                                            {
                                              validators: [baunebenkostenSummenValidator(), baunebenkostenBegruendungValidator()]
                                            });
  
  public readonly ctlBnkBerechnung = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNKBERECHNUNG)!;
  public readonly ctlBnkBerechnungID = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNKBERECHNUNGID)!;
  public readonly ctlBnkProzent = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNKPROZENT)!;
  public readonly ctlBnk1bis4Prozent = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNK1BIS4PROZENT)!;
  public readonly ctlBnk5bis9Prozent = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNK5BIS9PROZENT)!;
  public readonly ctlBnkVeraenderung = this.baunebenkostenForm.get(BudgetBaunebenkostenComponent.BNKVERAENDERUNG)!;

  public constructor() {

    effect(() => {
      var berechnung = this.dataBerechnungen()?.find((x) => x.berechnungID == (this.dataBudgetantrag().bnkBerechnungID ?? 0));

      this.baunebenkostenForm.patchValue({[BudgetBaunebenkostenComponent.BNKBERECHNUNG]: berechnung,
                                          [BudgetBaunebenkostenComponent.BNKBERECHNUNGID]: this.dataBudgetantrag().bnkBerechnungID,
                                          [BudgetBaunebenkostenComponent.BNKPROZENT]: this.dataBudgetantrag().bnkProzent,
                                          [BudgetBaunebenkostenComponent.BNK1BIS4PROZENT]: this.dataBudgetantrag().bnk1bis4Prozent, 
                                          [BudgetBaunebenkostenComponent.BNK5BIS9PROZENT]: this.dataBudgetantrag().bnk5bis9Prozent,
                                          [BudgetBaunebenkostenComponent.BNKVERAENDERUNG]: this.dataBudgetantrag().bnkVeraenderung});
    });
  }

  ngOnInit() {

    this.ctlBnkBerechnung.valueChanges.subscribe((value: IBerechnung) => {
      this.ctlBnkBerechnungID.setValue(value.berechnungID);
      this.ctlBnkProzent.setValue(value.bnk, { emitEvent: false });
      this.ctlBnk1bis4Prozent.setValue(value.bnk14, { emitEvent: false });
      this.ctlBnk5bis9Prozent.setValue(value.bnk59, { emitEvent: false });
      if (value.readonly) {
        this.ctlBnkProzent.disable({ emitEvent: false });
        this.ctlBnk1bis4Prozent.disable({ emitEvent: false });
        this.ctlBnk5bis9Prozent.disable({ emitEvent: false });
      } else {
        this.ctlBnkProzent.enable({ emitEvent: false });
        this.ctlBnk1bis4Prozent.enable({ emitEvent: false });
        this.ctlBnk5bis9Prozent.enable({ emitEvent: false }); 
      }

      this.validateBnkVeraenderung(value.bnk, value.bnk14, value.bnk59);
    });

    this.ctlBnkProzent.valueChanges.subscribe((value) => {
      if (value! < 0) this.ctlBnkProzent.setValue(0, { emitEvent: false });
      if (value! > 100) this.ctlBnkProzent.setValue(100, { emitEvent: false });

      this.validateBnkVeraenderung(value!, this.ctlBnk1bis4Prozent.value, this.ctlBnk5bis9Prozent.value);
    });

    this.ctlBnk1bis4Prozent.valueChanges.subscribe((value) => {
      if (value! < 0) this.ctlBnk1bis4Prozent.setValue(0, { emitEvent: false });
      if (value! > 100) this.ctlBnk1bis4Prozent.setValue(100, { emitEvent: false });

      this.validateBnkVeraenderung(this.ctlBnkProzent.value, value!, this.ctlBnk5bis9Prozent.value);
    });

    this.ctlBnk5bis9Prozent.valueChanges.subscribe((value) => {
      if (value! < 0) this.ctlBnk5bis9Prozent.setValue(0, { emitEvent: false });
      if (value! > 100) this.ctlBnk5bis9Prozent.setValue(100, { emitEvent: false });

      this.validateBnkVeraenderung(this.ctlBnkProzent.value, this.ctlBnk1bis4Prozent.value, value!);
    });

    this.baunebenkostenForm.statusChanges.subscribe((status) => {
      if (status === 'INVALID') {
        if (this.baunebenkostenForm.getError(BudgetBaunebenkostenComponent.ERROR_BNKSUMME)) {
          this.ctlBnk1bis4Prozent.setErrors({[BudgetBaunebenkostenComponent.ERROR_BNKSUMME]: true}, { emitEvent: false });
          this.ctlBnk1bis4Prozent.markAsTouched();  
          this.ctlBnk5bis9Prozent.setErrors({[BudgetBaunebenkostenComponent.ERROR_BNKSUMME]: true}, { emitEvent: false });
          this.ctlBnk5bis9Prozent.markAsTouched();
        } else {
          this.ctlBnk1bis4Prozent.setErrors(null, { emitEvent: false });
          this.ctlBnk5bis9Prozent.setErrors(null, { emitEvent: false });
        }
        
        if (this.baunebenkostenForm.getError(BudgetBaunebenkostenComponent.ERROR_BNKBEGRUENDUNG)) {
          this.ctlBnkVeraenderung.setErrors({[BudgetBaunebenkostenComponent.ERROR_BNKBEGRUENDUNG]: true}, { emitEvent: false });
          this.ctlBnkVeraenderung.markAsTouched();
        } else {
          this.ctlBnkVeraenderung.setErrors(null, { emitEvent: false });
        }
      } else {
        this.ctlBnk1bis4Prozent.setErrors(null, { emitEvent: false });
        this.ctlBnk5bis9Prozent.setErrors(null, { emitEvent: false });
        this.ctlBnkVeraenderung.setErrors(null, { emitEvent: false });
      }
    });
  }

  validateBnkVeraenderung(bnkProzent: number, bnk1bis4Prozent: number, bnk5bis9Prozent: number): boolean {
    
    if (this.ctlBnkBerechnungID.value == 2) {
      if (bnkProzent != this.ctlBnkBerechnung.value.bnk ||
          bnk1bis4Prozent != this.ctlBnkBerechnung.value.bnk14 ||
          bnk5bis9Prozent != this.ctlBnkBerechnung.value.bnk59) {
        this.ctlBnkVeraenderung.addValidators([Validators.required, Validators.minLength(1)]); // add validators if value is changed
        this.ctlBnkVeraenderung.updateValueAndValidity({ emitEvent: true });
        this.ctlBnkVeraenderung.markAsTouched();

        return false;
      }
    } 

    this.ctlBnkVeraenderung.clearValidators(); // remove validators if value is not changed
    this.ctlBnkVeraenderung.updateValueAndValidity({ emitEvent: true });
    this.ctlBnkVeraenderung.markAsTouched();

    return true;
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }

  isFormEditStateValid(): boolean {
    return (this.baunebenkostenForm.dirty && this.baunebenkostenForm.valid);
  }
  
  onFormSubmit() {

    // enable all controls to ensure they are included in the form value
    this.ctlBnkProzent.enable({ emitEvent: false });
    this.ctlBnk1bis4Prozent.enable({ emitEvent: false });
    this.ctlBnk5bis9Prozent.enable({ emitEvent: false }); 

    // we don't need the bnkBerechnung in the save data, as it is not part of the budgetantrag
    const {bnkBerechnung, ...baunebenkostenFormSave} = this.baunebenkostenForm.value;
    var antrag = {...this.dataBudgetantrag(), ...baunebenkostenFormSave};

    // save the form data to the budgetantrag
    this.dataService.updateBudgetantrag(this.appStateService.budgetantragId()!, antrag).subscribe({
      next: (dataBudgetantrag: HttpResponse<Budgetantrag>) => {
        this.dataService.budgetantragDetails.set(dataBudgetantrag.body!);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => { }
    });
  }

}

export function baunebenkostenSummenValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {

    const bnk1bis4Prozent: number = form.get(BudgetBaunebenkostenComponent.BNK1BIS4PROZENT)?.value;
    const bnk5bis9Prozent: number = form.get(BudgetBaunebenkostenComponent.BNK5BIS9PROZENT)?.value;

    if (bnk1bis4Prozent == null || bnk5bis9Prozent == null) {
      return { [BudgetBaunebenkostenComponent.ERROR_BNKSUMME]: true };
    }

    if (bnk1bis4Prozent != null && bnk5bis9Prozent != null) {
        const isSummeValid = (bnk1bis4Prozent + bnk5bis9Prozent == 100);
        return isSummeValid ? null : { [BudgetBaunebenkostenComponent.ERROR_BNKSUMME]: true };
    }

    return null;
  }
}

export function baunebenkostenBegruendungValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {

    const bnkBerechnungID: number = form.get(BudgetBaunebenkostenComponent.BNKBERECHNUNGID)?.value;
    const bnkBerechnung: IBerechnung = form.get(BudgetBaunebenkostenComponent.BNKBERECHNUNG)?.value;
    const bnkProzent: number = form.get(BudgetBaunebenkostenComponent.BNKPROZENT)?.value;
    const bnk1bis4Prozent: number = form.get(BudgetBaunebenkostenComponent.BNK1BIS4PROZENT)?.value;
    const bnk5bis9Prozent: number = form.get(BudgetBaunebenkostenComponent.BNK5BIS9PROZENT)?.value;
    const bnkVeraenderung: string = form.get(BudgetBaunebenkostenComponent.BNKVERAENDERUNG)?.value;

    // validate only "Eigene Berechnung" (ID 2)
    if (bnkBerechnungID == 2 ) {

      if (bnkVeraenderung != null && bnkVeraenderung.length >= 1) {
        return null;
      }
      if (bnkProzent == null || bnkProzent != bnkBerechnung.bnk) {
        return { [BudgetBaunebenkostenComponent.ERROR_BNKBEGRUENDUNG]: true };
      }
      if (bnk1bis4Prozent == null || bnk1bis4Prozent != bnkBerechnung.bnk14) {
        return { [BudgetBaunebenkostenComponent.ERROR_BNKBEGRUENDUNG]: true };
      }
      if (bnk5bis9Prozent == null || bnk5bis9Prozent != bnkBerechnung.bnk59) {
        return { [BudgetBaunebenkostenComponent.ERROR_BNKBEGRUENDUNG]: true };
      }
    }

    return null;
  }
}
